import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { AutoAwesome, CheckCircleOutline, Sync } from '@mui/icons-material';

interface Operation {
  id: string;
  estado: string;
  fase: string;
  docuware_estado: string | null;
  sinco_estado: string | null;
  mensaje: string | null;
}

interface Status {
  hora_servidor: string;
  ultima_sincronizacion_completada: string | null;
  proxima_sincronizacion: string | null;
  es_admin: boolean;
  puede_sincronizar: boolean;
  motivo_bloqueo: string | null;
  en_curso: Operation | null;
  ultima_operacion: Operation | null;
}

interface Props {
  codigo: number;
  csrfToken: string;
  canRefresh: boolean;
  onRefresh: () => Promise<boolean>;
  /** Attach a newly created request even if it finishes before the first poll. */
  watchOperationId?: string;
}

const fecha = (value: string) => new Intl.DateTimeFormat('es-CO', {
  dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/Bogota',
}).format(new Date(value));

// randomUUID requires HTTPS; getRandomValues also works on HTTP intranets.
export const newSyncRequestId = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const h = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
};

export const ProjectSyncCard: React.FC<Props> = ({ codigo, csrfToken, canRefresh, onRefresh, watchOperationId }) => {
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingRefresh, setPendingRefresh] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [refreshed, setRefreshed] = useState(false);
  const [now, setNow] = useState(Date.now());
  const offset = useRef(0);
  const lastOperation = useRef<string | undefined>(undefined);
  const watched = useRef(new Set<string>(watchOperationId ? [watchOperationId] : []));
  const refreshedIds = useRef(new Set<string>());
  const submittingId = useRef<string | null>(null);
  const runningRefresh = useRef(false);
  const failedRefresh = useRef(false);
  const refreshOperation = useRef<string | null>(null);
  const mounted = useRef(true);
  const postController = useRef<AbortController | null>(null);
  const active = useRef(false);
  const pollNow = useRef<() => void>(() => {});
  const endpoint = `/api/proyectos/${codigo}/sincronizacion`;

  const accept = useCallback((data: Status) => {
    offset.current = Date.parse(data.hora_servidor) - Date.now();
    setNow(Date.now());
    active.current = !!data.en_curso;
    if (data.en_curso) {
      watched.current.add(data.en_curso.id);
      setRefreshed(false);
    }
    const terminal = data.ultima_operacion;
    if (submittingId.current && (data.en_curso?.id === submittingId.current || terminal?.id === submittingId.current)) {
      submittingId.current = null;
    }
    if (!data.en_curso && terminal && !refreshedIds.current.has(terminal.id)
        && (watched.current.has(terminal.id)
          || (lastOperation.current !== undefined && lastOperation.current !== terminal.id))) {
      if (refreshOperation.current !== terminal.id) {
        refreshOperation.current = terminal.id;
        failedRefresh.current = false;
        setRefreshFailed(false);
      }
      setPendingRefresh(terminal.id);
    }
    lastOperation.current = terminal?.id || '';
    setStatus(data);
  }, []);

  useEffect(() => {
    mounted.current = true;
    let timer: ReturnType<typeof setTimeout>;
    let controller: AbortController | null = null;
    let stopped = false;
    let generation = 0;
    const poll = async () => {
      clearTimeout(timer);
      if (stopped || document.hidden) return;
      controller?.abort();
      controller = new AbortController();
      const current = ++generation;
      try {
        const response = await fetch(endpoint, { signal: controller.signal });
        const body = await response.json();
        if (!response.ok) throw new Error(body.error || 'No se pudo consultar la sincronización.');
        if (!stopped && current === generation) { accept(body); setError(''); }
      } catch (err) {
        if (!stopped && current === generation && err.name !== 'AbortError') setError(err.message);
      } finally {
        if (!stopped && current === generation) timer = setTimeout(poll, active.current ? 5000 : 60000);
      }
    };
    pollNow.current = () => { void poll(); };
    const visible = () => {
      if (document.hidden) { clearTimeout(timer); controller?.abort(); }
      else void poll();
    };
    void poll();
    const clock = setInterval(() => setNow(Date.now()), 1000);
    document.addEventListener('visibilitychange', visible);
    return () => {
      stopped = true;
      mounted.current = false;
      controller?.abort();
      postController.current?.abort();
      clearTimeout(timer);
      clearInterval(clock);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [endpoint, accept]);

  useEffect(() => {
    if (!pendingRefresh || !canRefresh || runningRefresh.current || failedRefresh.current
        || refreshedIds.current.has(pendingRefresh)) return;
    runningRefresh.current = true;
    setRefreshing(true);
    void onRefresh().then(ok => {
      if (!mounted.current) return;
      if (ok) {
        refreshedIds.current.add(pendingRefresh);
        setPendingRefresh(previous => previous === pendingRefresh ? null : previous);
        setRefreshed(true);
      } else if (refreshOperation.current === pendingRefresh) {
        failedRefresh.current = true;
        setRefreshFailed(true);
      }
    }).catch(() => {
      if (mounted.current && refreshOperation.current === pendingRefresh) {
        failedRefresh.current = true;
        setRefreshFailed(true);
      }
    }).finally(() => {
      runningRefresh.current = false;
      if (mounted.current) setRefreshing(false);
    });
  }, [pendingRefresh, canRefresh, onRefresh, refreshFailed]);

  const start = async () => {
    if (sending) return;
    setSending(true);
    setError('');
    setRefreshed(false);
    submittingId.current ||= newSyncRequestId();
    postController.current = new AbortController();
    try {
      const response = await fetch(endpoint, {
        method: 'POST', signal: postController.current.signal,
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ solicitud_id: submittingId.current }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'No se pudo solicitar la sincronización.');
      if (mounted.current) {
        watched.current.add(submittingId.current);
        accept(body);
        submittingId.current = null;
      }
    } catch (err) {
      // Keep the request UUID after a lost response: retrying cannot create a second run.
      if (mounted.current && err.name !== 'AbortError') setError(err.message);
    } finally {
      if (mounted.current) { setSending(false); pollNow.current(); }
    }
  };

  const operation = status?.en_curso;
  const remaining = status?.proxima_sincronizacion
    ? Math.max(0, Date.parse(status.proxima_sincronizacion) - now - offset.current) : 0;
  const cooldown = !status?.es_admin && remaining > 0;
  const cooldownExpired = status?.motivo_bloqueo === 'Debe esperar una hora entre sincronizaciones.' && !cooldown;
  const enabled = !!status && (status.puede_sincronizar || cooldownExpired) && !error;
  const phase = refreshing ? 3 : operation?.fase === 'sinco' ? 2
    : operation?.fase === 'docuware' ? 1 : operation?.fase === 'finalizando' ? 3 : 0;
  const working = !!operation || sending || refreshing;
  const stages = ['En cola', 'Documentos · DocuWare', 'SINCO · Clasificación', 'Actualizar vista'];
  const incident = !operation && status?.ultima_operacion && status.ultima_operacion.estado !== 'completado';

  return (
    <Box sx={{ mb: 2, p: { xs: 2, md: 2.5 }, borderRadius: 3, border: '1px solid',
      borderColor: working ? 'primary.light' : 'divider',
      background: working ? 'linear-gradient(120deg, #f0f7ff, #f7f4ff, #f0f7ff)' : 'background.paper',
      backgroundSize: '200% 200%', animation: working ? 'syncGlow 7s ease infinite' : 'none',
      '@keyframes syncGlow': { '0%,100%': { backgroundPosition: '0% 50%' }, '50%': { backgroundPosition: '100% 50%' } },
      '@media (prefers-reduced-motion: reduce)': { animation: 'none', '& *': { animation: 'none !important' } },
    }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <Button variant="contained" startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <Sync />}
          disabled={!enabled || working || !!pendingRefresh} onClick={start} sx={{ flexShrink: 0 }}>
          {working ? 'Sincronizando…' : 'Sincronizar datos'}
        </Button>
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {status?.ultima_sincronizacion_completada
              ? `Última sincronización completada: ${fecha(status.ultima_sincronizacion_completada)}`
              : status ? 'Sin historial de sincronización registrado' : 'Consultando sincronización…'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {operation ? 'Puedes seguir trabajando. Actualizaremos este reporte al terminar.'
              : status?.motivo_bloqueo && status.motivo_bloqueo !== 'Debe esperar una hora entre sincronizaciones.'
                ? status.motivo_bloqueo
              : cooldown ? `Disponible en ${Math.ceil(remaining / 60000)} min · ${fecha(status.proxima_sincronizacion)}`
              : status?.es_admin ? 'Administrador · sin espera entre ejecuciones'
              : cooldownExpired ? 'DocuWare y SINCO · solo para este centro de costos'
              : status?.motivo_bloqueo || 'DocuWare y SINCO · solo para este centro de costos'}
          </Typography>
        </Box>
      </Stack>
      {working && <Box role="status" aria-live="polite" sx={{ mt: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <AutoAwesome color="primary" fontSize="small" />
          <Typography variant="body2" fontWeight={600}>
            {sending ? 'Preparando tu solicitud' : refreshing ? 'Preparando los datos de tu reporte'
              : phase === 0 ? 'Tu proyecto está en la fila de sincronización'
              : phase === 1 ? 'Buscando documentos y novedades en DocuWare'
              : phase === 2 ? 'Consultando SINCO y clasificando tus facturas' : 'Finalizando la sincronización'}
          </Typography>
        </Stack>
        <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
          {stages.map((label, index) => <Chip key={label} size="small" label={label}
            color={index === phase ? 'primary' : 'default'} variant={index === phase ? 'filled' : 'outlined'}
            icon={index === phase ? <CircularProgress size={12} color="inherit" />
              : (index === 1 && operation?.docuware_estado === 'COMPLETED')
                || (index === 2 && operation?.sinco_estado === 'COMPLETED') ? <CheckCircleOutline /> : undefined} />)}
        </Stack>
        {operation?.mensaje && <Typography variant="caption">{operation.mensaje}</Typography>}
      </Box>}
      {!!pendingRefresh && !refreshing && !refreshFailed && <Alert severity="info" sx={{ mt: 2 }}>
        Sincronización terminada. Actualizaremos la vista cuando termines de editar.
      </Alert>}
      {refreshFailed && <Alert severity="warning" sx={{ mt: 2 }} action={
        <Button color="inherit" size="small" onClick={() => {
          failedRefresh.current = false;
          setRefreshFailed(false);
        }}>Reintentar vista</Button>
      }>La sincronización terminó, pero no pudimos actualizar el reporte.</Alert>}
      {incident && <Alert severity="warning" sx={{ mt: 2 }}>
        La última sincronización terminó con incidencias. Los cambios parciales se conservan.
      </Alert>}
      {refreshed && !incident && !working && <Typography role="status" variant="body2" color="success.main" sx={{ mt: 1.5 }}>
        Sincronización completada. Tu reporte está actualizado.
      </Typography>}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </Box>
  );
};
